import { useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { dm, dmSv } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { ProcessBar, ProcessSteps } from '../process.js';
import { navigate } from '../router.js';
import { NewProjectDialog } from './Projects.js';
import { AddBall, PersonPicker } from '../personPicker.js';
import { toast } from '../ui.js';

/**
 * A new client's start in five steps, like a project's stages (owner 2026-10-28): agreement, handover, set-up (Drive
 * folders and client brief), client portal, first project. The API reads every step from what happened in the Hub.
 */

export interface ClientStartView {
  /** new: a new client's five steps; hub: an existing client getting going in the Hub, four steps */
  kind: 'new' | 'hub';
  done: number;
  running: boolean;
  agreement: { id: string; ref: string; signedOn: string | null } | null;
  handover: { at: string; producerId: string } | null;
  producer: string | null;
  folders: 'ready' | 'none' | 'off';
  brief: boolean;
  portal: { contacts: number; invited: number; signedIn: number };
  projects: number;
  steps: boolean[];
}

const TX = texts(
  {
    label: 'Getting the client started',
    steps: ['Agreement', 'Handover', 'Set-up', 'Client portal', 'First project'],
    hubLabel: 'Up and running in the Hub',
    hubSteps: ['Producer', 'Client brief', 'Drive folders', 'Client portal'],
    hubWhy: 'What the Hub still lacks for this client after the move from Monday.',
    producerIs: (n: string) => n,
    noProducer: 'No producer for the client',
    pickProducer: 'Choose producer',
    save: 'Save',
    foldersMake: 'Made by Make (Drive folders are off in the Hub)',
    findFolders: 'Find folders',
    running: 'Running',
    nextStep: (n: number, name: string) => `Next: ${n} ${name}`,
    signed: (ref: string, d: string | null) => `Quote ${ref} signed${d ? ` ${d}` : ''}`,
    handedTo: (n: string, d: string) => `To ${n} ${d}`,
    notHanded: 'Not handed over to a producer',
    handOver: 'Hand over',
    folders: 'Drive folders',
    noFolders: 'No Drive folders yet',
    brief: 'Client brief written',
    noBrief: 'Client brief not written',
    writeBrief: 'Write the client brief',
    makeFolders: 'Create folders',
    invited: (n: number, of: number) => `${n} of ${of} contacts invited`,
    nobodyInvited: 'Nobody invited yet',
    signedIn: (n: number) => (n ? `${n} signed in` : 'Nobody signed in yet'),
    invite: 'Invite',
    nProjects: (n: number) => `${n} ${n === 1 ? 'project' : 'projects'}`,
    noProject: 'No project yet',
    newProject: 'New project'
  },
  {
    label: 'Uppstart av kunden',
    steps: ['Avtal', 'Överlämning', 'Uppstart', 'Kundportal', 'Första projekt'],
    hubLabel: 'Igång i Hubben',
    hubSteps: ['Producent', 'Kundbrief', 'Drive-mappar', 'Kundportal'],
    hubWhy: 'Det som fortfarande saknas i Hubben för kunden efter flytten från Monday.',
    producerIs: (n: string) => n,
    noProducer: 'Ingen producent för kunden',
    pickProducer: 'Välj producent',
    save: 'Spara',
    foldersMake: 'Görs av Make (Drive-mappar är av i Hubben)',
    findFolders: 'Hitta mappar',
    running: 'Igång',
    nextStep: (n: number, name: string) => `Nästa: ${n} ${name}`,
    signed: (ref: string, d: string | null) => `Offert ${ref} signerad${d ? ` ${d}` : ''}`,
    handedTo: (n: string, d: string) => `Till ${n} ${d}`,
    notHanded: 'Inte överlämnad till en producent',
    handOver: 'Lämna över',
    folders: 'Drive-mappar',
    noFolders: 'Inga Drive-mappar än',
    brief: 'Kundbrief skriven',
    noBrief: 'Kundbrief inte skriven',
    writeBrief: 'Skriv kundbrief',
    makeFolders: 'Skapa mappar',
    invited: (n: number, of: number) => `${n} av ${of} kontakter inbjudna`,
    nobodyInvited: 'Ingen inbjuden än',
    signedIn: (n: number) => (n ? `${n} inloggade` : 'Ingen inloggad än'),
    invite: 'Bjud in',
    nProjects: (n: number) => `${n} projekt`,
    noProject: 'Inget projekt än',
    newProject: 'Nytt projekt'
  }
);

const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));
const toBlock = (id: string) => () => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export function ClientStartBlock(p: { api: Api; clientId: string; s: ClientStartView; canEdit: boolean; onChanged: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [creating, setCreating] = useState(false);
  const s = p.s;
  if (s.running) return null;
  if (s.kind === 'hub' || !s.agreement) return <HubStart {...p} />;
  const d = (iso: string) => (lang === 'sv' ? dmSv : dm)(iso.slice(0, 10));
  const next = s.steps.findIndex((x) => !x);
  const btn = (label: string, run: () => void, primary = true) =>
    p.canEdit ? (
      <button type="button" class={`btn btn-sm${primary ? ' btn-primary' : ''}`} onClick={run}>
        {label}
      </button>
    ) : null;
  const agreementUrl = `/agreements/${s.agreement.id}`;
  const agreement = s.agreement;
  return (
    <section class="blk cc-start" aria-labelledby="cs-h">
      <div class="cc-start-head">
        <h2 class="blk-title" id="cs-h">
          {t.label}
        </h2>
        <ProcessBar done={s.done} label={t.nextStep(next + 1, t.steps[next] ?? '')} />
      </div>
      <ProcessSteps
        label={t.label}
        steps={[
          { title: t.steps[0]!, done: s.steps[0]!, lines: [{ text: t.signed(agreement.ref, agreement.signedOn ? d(agreement.signedOn) : null), done: true }] },
          {
            title: t.steps[1]!,
            done: s.steps[1]!,
            lines: [s.handover ? { text: t.handedTo(s.producer ?? '–', d(s.handover.at)), done: true } : { text: t.notHanded, done: false }],
            action: p.canEdit ? (
              <a class="btn btn-sm btn-primary" href={agreementUrl} onClick={go(agreementUrl)}>
                {t.handOver}
              </a>
            ) : null
          },
          {
            title: t.steps[2]!,
            done: s.steps[2]!,
            lines: [...(s.folders === 'off' ? [] : [{ text: s.folders === 'ready' ? t.folders : t.noFolders, done: s.folders === 'ready' }]), { text: s.brief ? t.brief : t.noBrief, done: s.brief }],
            action: !s.brief ? btn(t.writeBrief, toBlock('cb-h')) : btn(t.makeFolders, toBlock('cc-d'))
          },
          {
            title: t.steps[3]!,
            done: s.steps[3]!,
            lines: [s.portal.invited ? { text: t.invited(s.portal.invited, s.portal.contacts), done: true } : { text: t.nobodyInvited, done: false }, { text: t.signedIn(s.portal.signedIn) }],
            action: btn(t.invite, toBlock('cc-c'))
          },
          { title: t.steps[4]!, done: s.steps[4]!, lines: [s.projects ? { text: t.nProjects(s.projects), done: true } : { text: t.noProject, done: false }], action: btn(t.newProject, () => setCreating(true)) }
        ]}
      />
      {creating ? <NewProjectDialog api={p.api} clientId={p.clientId} onClose={() => setCreating(false)} /> : null}
    </section>
  );
}

/** "Igång i Hubben": an existing client's four steps (owner 2026-10-28). */
function HubStart(p: { api: Api; clientId: string; s: ClientStartView; canEdit: boolean; onChanged: () => void }) {
  const t = TX[useLang()];
  const s = p.s;
  const next = s.steps.findIndex((x) => !x);
  const btn = (label: string, run: () => void) =>
    p.canEdit ? (
      <button type="button" class="btn btn-sm btn-primary" onClick={run}>
        {label}
      </button>
    ) : null;
  return (
    <section class="blk cc-start" aria-labelledby="cs-h">
      <div class="cc-start-head">
        <h2 class="blk-title" id="cs-h">
          {t.hubLabel}
        </h2>
        <ProcessBar done={s.done} of={4} label={t.nextStep(next + 1, t.hubSteps[next] ?? '')} />
      </div>
      <p class="small muted">{t.hubWhy}</p>
      <ProcessSteps
        label={t.hubLabel}
        steps={[
          {
            title: t.hubSteps[0]!,
            done: s.steps[0]!,
            lines: [s.producer ? { text: t.producerIs(s.producer), done: true } : { text: t.noProducer, done: false }],
            action: p.canEdit ? <PickProducer api={p.api} clientId={p.clientId} onSaved={p.onChanged} /> : null
          },
          { title: t.hubSteps[1]!, done: s.steps[1]!, lines: [{ text: s.brief ? t.brief : t.noBrief, done: s.brief }], action: btn(t.writeBrief, toBlock('cb-h')) },
          {
            title: t.hubSteps[2]!,
            done: s.steps[2]!,
            lines: [s.folders === 'off' ? { text: t.foldersMake } : { text: s.folders === 'ready' ? t.folders : t.noFolders, done: s.folders === 'ready' }],
            action: btn(t.findFolders, toBlock('cc-d'))
          },
          {
            title: t.hubSteps[3]!,
            done: s.steps[3]!,
            lines: [s.portal.invited ? { text: t.invited(s.portal.invited, s.portal.contacts), done: true } : { text: t.nobodyInvited, done: false }, { text: t.signedIn(s.portal.signedIn) }],
            action: btn(t.invite, toBlock('cc-c'))
          }
        ]}
      />
    </section>
  );
}

function PickProducer(p: { api: Api; clientId: string; onSaved: () => void }) {
  const t = TX[useLang()];
  const [at, setAt] = useState<HTMLElement | null>(null);
  const [list, setList] = useState<{ id: string; name: string }[] | null>(null);
  const open = (el: HTMLElement) => {
    setAt(el);
    if (!list)
      p.api.get<{ people: { producer: { id: string; name: string }[] } }>('/api/projects/options').then(
        (o) => setList(o.people.producer),
        (e) => (toast({ text: e instanceof Error ? e.message : String(e) }), setAt(null))
      );
  };
  // the dashed ball and the same search list as a project's team (owner 2026-11-01)
  return (
    <>
      <button type="button" class={`sh-chip-add${at ? ' is-open' : ''}`} aria-expanded={Boolean(at)} onClick={(e) => (at ? setAt(null) : open(e.currentTarget as HTMLElement))}>
        <AddBall size="sm" />
        {t.pickProducer}
      </button>
      {at ? <PersonPicker label={t.pickProducer} people={list} current={[]} multi={false} at={at} onClose={() => setAt(null)} onPick={async ([id]) => (id ? (await p.api.put(`/api/clients/${p.clientId}/producer`, { producerId: id }), p.onSaved()) : undefined)} /> : null}
    </>
  );
}

/** The Clients list: the bar and the next step for a client still getting going (new client, or in the Hub). */
export function ClientStartCell(p: { start: { kind?: 'new' | 'hub'; done: number; of?: number; next: number } }) {
  const t = TX[useLang()];
  const names = p.start.kind === 'hub' ? t.hubSteps : t.steps;
  return <ProcessBar done={p.start.done} of={p.start.of ?? 5} label={p.start.next < 0 ? t.running : t.nextStep(p.start.next + 1, names[p.start.next] ?? '')} />;
}
